/**
 * D3.js Planet Animation System
 * 
 * Leverages existing D3 v7.8.5 infrastructure and optimized utilities
 * for high-performance planet sprite animations
 */

import { devLog } from '../../../../../utils/devLog';
import * as d3 from 'd3';
import { batchD3Operations } from '../../../../../utils/optimization/optimizedD3';

export interface PlanetSprite {
  // Unified container for all planet elements
  container: d3.Selection<SVGGElement, unknown, any, any>;
  
  // Separate group for connection line (can be rotated independently)
  connectionGroup: d3.Selection<SVGGElement, unknown, any, any>;
  
  // Sprite elements (created once)
  circle: d3.Selection<SVGCircleElement, unknown, any, any>;
  text: d3.Selection<SVGTextElement, unknown, any, any>;
  connectionLine: d3.Selection<SVGLineElement, unknown, any, any>;
  
  // Sprite state
  position: { x: number, y: number };
  rotation: number;  // For corner houses
  side: 'top' | 'bottom' | 'left' | 'right';  // For flips
  planetName: string;
  containerIndex: number;
  horaCurrentPlanet?: string;  // Store hora current planet for color updates
  planetFullName?: string;     // Store full planet name for hora detection
}

export interface AnimationConfig {
  duration: number;
  easing: (t: number) => number;
  delay?: number;
}

export class D3PlanetAnimator {
  
  // Default animation configurations
  public static readonly ANIMATION_PRESETS = {
    FAST: { duration: 100, easing: d3.easeCubicOut },
    NORMAL: { duration: 200, easing: d3.easeCubicOut },
    SMOOTH: { duration: 500, easing: d3.easeCubicInOut },
    ELASTIC: { duration: 800, easing: d3.easeElastic },
    BOUNCE: { duration: 600, easing: d3.easeBounce }
  } as const;

  /**
   * Animate planet sprite position with modern D3 transitions
   */
  public static animatePlanetSprite(
    sprite: PlanetSprite,
    newPosition: { x: number, y: number },
    newRotation: number = 0,
    config: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.NORMAL
  ): Promise<void> {
    
    return new Promise((resolve) => {
      sprite.container
        .transition()
        .duration(config.duration)
        .delay(config.delay || 0)
        .ease(config.easing)
        .tween('planetAnimation', () => {
          const positionInterpolator = d3.interpolate(sprite.position, newPosition);
          const rotationInterpolator = d3.interpolateNumber(sprite.rotation, newRotation);
          
          return (t: number) => {
            const currentPosition = positionInterpolator(t);
            const currentRotation = rotationInterpolator(t);
            
            // Update position on main container
            sprite.container.attr('transform', `translate(${currentPosition.x}, ${currentPosition.y})`);
            
            // Update rotation only on connection group
            sprite.connectionGroup.attr('transform', `rotate(${currentRotation})`);
          };
        })
        .on('end', () => {
          // Update sprite state
          sprite.position = newPosition;
          sprite.rotation = newRotation;
          resolve();
        });
    });
  }

  /**
   * Flip animation for side changes (revolutionary approach)
   */
  public static flipPlanetSprite(
    sprite: PlanetSprite,
    newSide: 'top' | 'bottom' | 'left' | 'right',
    config: AnimationConfig = { duration: 150, easing: d3.easeQuadOut }
  ): Promise<void> {
    
    const flipTransforms = {
      'top': 'scaleY(-1)',
      'bottom': 'scaleY(1)', 
      'left': 'scaleX(-1)',
      'right': 'scaleX(1)'
    };
    
    const currentTransform = sprite.container.attr('transform') || '';
    const baseTransform = currentTransform.replace(/scale[XY]\([^)]*\)/g, '').trim();
    
    return new Promise((resolve) => {
      // Two-phase flip animation
      sprite.container
        .transition()
        .duration(config.duration / 2)
        .ease(d3.easeQuadOut)
        .attr('transform', `${baseTransform} scale(0.1, 1)`) // Shrink horizontally
        .transition()
        .duration(config.duration / 2)
        .ease(d3.easeQuadIn)
        .attr('transform', `${baseTransform} ${flipTransforms[newSide]}`)
        .on('end', () => {
          sprite.side = newSide;
          resolve();
        });
    });
  }

  /**
   * Batch update multiple sprites efficiently using existing optimizedD3 utilities
   */
  public static updateMultipleSprites(
    updates: Array<{
      sprite: PlanetSprite;
      position: { x: number, y: number };
      rotation?: number;
    }>,
    config: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.FAST
  ): Promise<void[]> {
    
    // Use existing batchD3Operations utility for performance
    const operations = updates.map(update => () => {
      return D3PlanetAnimator.animatePlanetSprite(
        update.sprite,
        update.position,
        update.rotation || 0,
        config
      );
    });

    const batchedOperation = batchD3Operations(operations);
    
    // Execute batched operations and return promises
    return new Promise((resolve) => {
      batchedOperation();
      
      // Collect all animation promises
      const promises = updates.map(update => 
        D3PlanetAnimator.animatePlanetSprite(
          update.sprite,
          update.position,
          update.rotation || 0,
          config
        )
      );
      
      Promise.all(promises).then(resolve);
    });
  }

  /**
   * Chain multiple animations in sequence
   */
  public static chainAnimations(
    sprite: PlanetSprite,
    animations: Array<{
      position: { x: number, y: number };
      rotation?: number;
      config?: AnimationConfig;
    }>
  ): Promise<void> {
    
    return animations.reduce((promise, animation) => {
      return promise.then(() => 
        D3PlanetAnimator.animatePlanetSprite(
          sprite,
          animation.position,
          animation.rotation || sprite.rotation,
          animation.config || D3PlanetAnimator.ANIMATION_PRESETS.NORMAL
        )
      );
    }, Promise.resolve());
  }

  /**
   * Interrupt and reset sprite to position immediately
   */
  public static stopAndResetSprite(
    sprite: PlanetSprite,
    position: { x: number, y: number },
    rotation: number = 0
  ): void {
    
    // Stop any ongoing transitions
    sprite.container.interrupt();
    
    // Set final position and rotation immediately
    sprite.container.attr('transform', `translate(${position.x}, ${position.y})`);
    sprite.connectionGroup.attr('transform', `rotate(${rotation})`);
    
    // Update sprite state
    sprite.position = position;
    sprite.rotation = rotation;
  }

  /**
   * Create smooth interpolated path animation for complex movements
   */
  public static animateAlongPath(
    sprite: PlanetSprite,
    waypoints: Array<{ x: number, y: number, rotation?: number }>,
    totalDuration: number = 1000,
    easing: (t: number) => number = d3.easeCubicInOut
  ): Promise<void> {
    
    if (waypoints.length < 2) {
      throw new Error('Need at least 2 waypoints for path animation');
    }

    return new Promise((resolve) => {
      sprite.container
        .transition()
        .duration(totalDuration)
        .ease(easing)
        .tween('pathMove', () => {
          
          // Create interpolators for position and rotation
          const positionInterpolators = waypoints.slice(0, -1).map((point, i) => {
            return d3.interpolate(point, waypoints[i + 1]);
          });
          
          const rotationInterpolators = waypoints.slice(0, -1).map((point, i) => {
            const startRotation = point.rotation || 0;
            const endRotation = waypoints[i + 1].rotation || 0;
            return d3.interpolateNumber(startRotation, endRotation);
          });
          
          return (t: number) => {
            // Calculate which segment we're in
            const segmentCount = waypoints.length - 1;
            const segmentProgress = t * segmentCount;
            const segmentIndex = Math.floor(segmentProgress);
            const segmentT = segmentProgress - segmentIndex;
            
            // Clamp to valid range
            const clampedIndex = Math.min(segmentIndex, segmentCount - 1);
            
            // Interpolate position and rotation
            const position = positionInterpolators[clampedIndex](segmentT);
            const rotation = rotationInterpolators[clampedIndex](segmentT);
            
            // Update position on main container
            sprite.container.attr('transform', `translate(${position.x}, ${position.y})`);
            
            // Update rotation only on connection group
            sprite.connectionGroup.attr('transform', `rotate(${rotation})`);
          };
        })
        .on('end', () => {
          // Update final state
          const finalPoint = waypoints[waypoints.length - 1];
          sprite.position = { x: finalPoint.x, y: finalPoint.y };
          sprite.rotation = finalPoint.rotation || 0;
          resolve();
        });
    });
  }

  /**
   * Performance monitoring for animations
   */
  public static measureAnimationPerformance<T>(
    animationName: string,
    animationFunction: () => Promise<T>
  ): Promise<T> {
    
    const startTime = performance.now();
    
    return animationFunction().then(result => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (process.env.NODE_ENV === 'development') {
        devLog(`[D3Animation] ${animationName}: ${duration.toFixed(2)}ms`);
      }
      
      return result;
    });
  }
}